import { useState, useMemo } from "react";
import LogoOpenAI from "../../../../assets/icons/LogoOpenAI";
import LogoClaude from "../../../../assets/icons/LogoClaude";
import TrashIcon from "../../../../assets/icons/TrashIcon";
import ArrowUpIcon from "../../../../assets/icons/ArrowUpIcon";
import ArrowDownIcon from "../../../../assets/icons/ArrowDownIcon";
import UserIcon from "../../../../assets/icons/UserIcon";
import ChevronRightIcon from "../../../../assets/icons/ChevronRightIcon";
import ChevronDownIcon from "../../../../assets/icons/ChevronDownIcon";
import ExternalLinkIcon from "../../../../assets/icons/ExternalLinkIcon";
import Message from "../../../../i18n/Message";

import "./ChatMessage.scss";

import MessageTextContainer from "./MessageTextContainer";
import Edit2Icon from "../../../../assets/icons/Edit2Icon";
import { useDispatch, useSelector } from "react-redux";
import { useWorkspaceId } from "../../../shared/workspace/workspaceContext";
import {
  deleteChatMessage,
  updateChatMessage,
} from "../store/chatActionCreators";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import CrossIcon from "../../../../assets/icons/CrossIcon";
import CheckIcon from "../../../../assets/icons/CheckIcon";
import Spinner from "../../../../assets/icons/Spinner";
import ClipboardIcon from "../../../../assets/icons/ClipboardIcon";
import Notify from "../../../shared/Notify";
import label from "../../../../i18n/label";
import FileTextIcon from "../../../../assets/icons/FileTextIcon";
import modelDescriptors, {
  currentModelDescriptions,
} from "../../../../model/model-providers/modelDescriptors";
import ModelVendors from "../../../../model/model-providers/modelVendors";
import ToolUseRequest from "./tool-request/ToolUseRequest";
import { toolPluginSelectors } from "../../tools/store/toolPluginReducer";
import ThinkingBlocks from "./thinking/ThinkingBlocks";
import { chatSelectors } from "../store/chatReducer";
import NebiusChatAvatar from "./NebiusChatAvatar";
import AgenticTimeline from "./agentic/AgenticTimeline";

const ChatMessage = ({
  message,
  chat,
  onApproveToolUse,
  onRejectToolUse,
  isLastMessage = false,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const workspaceId = useWorkspaceId();
  const { chatId } = useParams();

  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [content, setContent] = useState(message.content);
  const [messageElem, setMessageElem] = useState(false);
  const [messageHeight, setMessageHeight] = useState(null);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [messageCopied, setMessageCopied] = useState(false);
  const [referencesExpanded, setReferencesExpanded] = useState(false);
  const toolPluginList = useSelector(toolPluginSelectors.getList);
  const allMessages = useSelector(chatSelectors.getMessages);

  const model = currentModelDescriptions.find(
    (item) => item.id === chat.modelId
  );
  const oldNodel = modelDescriptors.find((item) => item.id === chat.modelId);

  const vendor = model?.vendor || oldNodel?.vendor;

  const onDeleteConfirmed = () => {
    dispatch(deleteChatMessage(intl, workspaceId, chatId, message.uid));
    setDeleting(false);
  };
  const startEditing = () => {
    if (messageElem) {
      const messageHeight = messageElem.getBoundingClientRect().height;
      setMessageHeight(messageHeight + 32);
    }
    setEditing(true);
  };
  const cancelEditing = () => {
    setMessageHeight(null);
    setContent(message.content);
    setEditing(false);
  };
  const onContentChanged = (e) => {
    console.log("Content changed: ", e.target.value);
    setContent(e.target.value);
  };
  const saveEditing = () => {
    console.log("On saving...");
    if (!content) return;

    const onSuccess = () => {
      setMessageHeight(null);
      setEditing(false);
      setSaveInProgress(false);
      setHovering(false);
    };
    const onError = () => {
      setSaveInProgress(false);
    };
    setSaveInProgress(true);
    dispatch(
      updateChatMessage(
        intl,
        workspaceId,
        chatId,
        message.uid,
        content,
        onSuccess,
        onError
      )
    );
  };
  const onTextareaBlur = (e) => {
    console.log(
      "onTextareaBlur ======> ",
      messageElem,
      e.target,
      messageElem.contains(e.target),
      document.activeElement
    );
    if (content === message.content) {
      cancelEditing();
    }
  };
  //
  const onCopy = () => {
    if (!navigator.clipboard) {
      Notify.error(label(intl, "clipboard_copy_failed"));
      return;
    }

    navigator.clipboard.writeText(message.content).then(() => {
      setMessageCopied(true);
      setTimeout(() => {
        setMessageCopied(false);
      }, 3000);
    });
  };

  const startDeleting = () => {
    setDeleting(true);
    setTimeout(() => setDeleting(false), 5000);
  };

  // Parse thinking tokens from JSON - memoized to avoid parsing on every render
  const thinkingBlocks = useMemo(() => {
    if (!message.thinkingTokensJson) return null;

    try {
      const thinkingTokens = JSON.parse(message.thinkingTokensJson);
      // Convert to format expected by ThinkingBlocks component
      return thinkingTokens.map((token) => ({
        blockIndex: token.blockIndex,
        content: token.text,
        durationMs: token.durationMs,
        isComplete: true, // Persisted blocks are always complete
        isExpanded: false,
      }));
    } catch (e) {
      console.error("Failed to parse thinking tokens:", e);
      return null;
    }
  }, [message.thinkingTokensJson]);

  // Parse RAG references from JSON - memoized to avoid parsing on every render
  const ragReferences = useMemo(() => {
    if (!message.ragReferences) return null;

    try {
      const references = JSON.parse(message.ragReferences);
      return Array.isArray(references) && references.length > 0
        ? references
        : null;
    } catch (e) {
      console.error("Failed to parse RAG references:", e);
      return null;
    }
  }, [message.ragReferences]);

  // Parse agentic timeline from JSON - memoized to avoid parsing on every render
  const agenticTimelineData = useMemo(() => {
    if (!message.agenticTimelineJson) return null;

    try {
      const timelineData = JSON.parse(message.agenticTimelineJson);
      return timelineData;
    } catch (e) {
      console.error("Failed to parse agentic timeline:", e);
      return null;
    }
  }, [message.agenticTimelineJson]);

  // Helper function to find tool response from subsequent user messages
  const findToolResponse = (toolUseId) => {
    if (!allMessages || !message.uid) return null;

    // Find the current message index
    const currentIndex = allMessages.findIndex(
      (msg) => msg.uid === message.uid
    );
    if (currentIndex === -1) return null;

    // Look ahead through subsequent messages
    for (let i = currentIndex + 1; i < allMessages.length; i++) {
      const laterMsg = allMessages[i];

      // Only check user messages with toolUse field
      if (laterMsg.role === "user" && laterMsg.toolUse) {
        try {
          const toolUse = JSON.parse(laterMsg.toolUse);
          // Check if this is the matching tool use by ID
          if (toolUse.toolUseId === toolUseId) {
            return laterMsg.toolUseResponse || null;
          }
        } catch (e) {
          console.error("Failed to parse toolUse:", e);
        }
      }

      // Stop looking when we hit another assistant message (boundary)
      if (laterMsg.role === "assistant") {
        break;
      }
    }

    return null;
  };

  //console.log("message ======> ", message);
  return (
    <>
      <div
        className={
          "chat_message " +
          (message.role === "user" ? "user" : "") +
          (message.role === "assistant" ? "assistant" : "")
        }
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        <div className="avatar">
          {message.role === "user" && (
            <div className="user_avatar">
              <UserIcon color="#777" />
            </div>
          )}
          {message.role === "assistant" && vendor === ModelVendors.OPENAI && (
            <div className="openai_avatar">
              <LogoOpenAI color="#fff" />
            </div>
          )}
          {message.role === "assistant" &&
            vendor === ModelVendors.ANTHROPIC && (
              <div className="claude_avatar">
                <LogoClaude color="#fff" size={24} />
              </div>
            )}
          {message.role === "assistant" && vendor === ModelVendors.NEBIUS && (
            <NebiusChatAvatar modelId={chat.modelId} />
          )}
        </div>
        <div className="message_item" ref={(item) => setMessageElem(item)}>
          {!editing && (
            <>
              {agenticTimelineData && (
                <AgenticTimeline
                  timeline={agenticTimelineData.timeline}
                  sessionStatus={agenticTimelineData.sessionStatus}
                />
              )}
              {thinkingBlocks && thinkingBlocks.length > 0 && (
                <ThinkingBlocks blocks={thinkingBlocks} />
              )}
              <MessageTextContainer>{message.content}</MessageTextContainer>
              {message.toolUseRequests && (
                <div className="tool_use_requests">
                  {JSON.parse(message.toolUseRequests).map((toolUse) => {
                    const plugin = toolPluginList?.find((plugin) => {
                      const toolsJson = JSON.parse(plugin.mcpToolsJson);
                      return toolsJson.find(
                        (tool) => tool.name === toolUse.toolName
                      );
                    });
                    const pluginName = plugin ? plugin.name : "";

                    // Look ahead to find the tool response from subsequent user messages
                    const toolResponse = findToolResponse(toolUse.toolUseId);

                    return (
                      <ToolUseRequest
                        key={toolUse.toolUseId}
                        toolUse={toolUse}
                        pluginName={pluginName}
                        messageUid={message.uid}
                        isExpanded={!message.toolUseAction}
                        toolUseAction={message.toolUseAction}
                        onApprove={onApproveToolUse}
                        onReject={onRejectToolUse}
                        toolResponse={toolResponse}
                        allowActions={isLastMessage}
                      />
                    );
                  })}
                </div>
              )}
              {ragReferences && message.role === "assistant" && (
                <div className="rag_references_section">
                  <div
                    className="references_header"
                    onClick={() => setReferencesExpanded(!referencesExpanded)}
                  >
                    {referencesExpanded ? (
                      <ChevronDownIcon
                        size={16}
                        color="#67748e"
                        className="chevron_icon"
                      />
                    ) : (
                      <ChevronRightIcon
                        size={16}
                        color="#67748e"
                        className="chevron_icon"
                      />
                    )}
                    <span className="references_label">
                      References ({ragReferences.length})
                    </span>
                  </div>
                  {referencesExpanded && (
                    <div className="references_list">
                      {ragReferences.map((ref, index) => (
                        <div key={index} className="reference_item">
                          {ref.assetType === "URL" ? (
                            <>
                              <ExternalLinkIcon
                                size={16}
                                color="#67748e"
                                strokeWidth="2"
                              />
                              <a
                                href={ref.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="reference_link"
                              >
                                {ref.name || ref.url}
                              </a>
                            </>
                          ) : (
                            <>
                              <FileTextIcon size={16} color="#67748e" />
                              <span className="reference_name">
                                {ref.name || ref.filename}
                              </span>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {editing && (
            <div className="editing_container">
              <textarea
                className="form-control "
                autoFocus={true}
                disabled={saveInProgress}
                style={{
                  height: messageHeight ? messageHeight + "px" : "auto",
                }}
                onChange={onContentChanged}
                onBlur={onTextareaBlur}
                value={content}
              ></textarea>
              <div className="edit_actions">
                <button
                  className="btn btn-sm bg-gradient-dark tile-action"
                  disabled={saveInProgress}
                  onClick={saveEditing}
                >
                  {!saveInProgress && (
                    <CheckIcon size="16" color="#fff" strokeWidth="2" />
                  )}
                  {saveInProgress && <Spinner size="16" color="#fff" />}
                  <Message id="save" />
                </button>
                <button
                  className="btn btn-sm no-btn tile-action"
                  onClick={cancelEditing}
                >
                  <CrossIcon size="16" color="#344767" />
                  <Message id="cancel" />
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="message_metadata">
          {!!message.filename && (
            <>
              <div className="message_metadata_item">
                <FileTextIcon color="rgb(109, 123, 147)" size="16" />
                {message.filename}
              </div>
            </>
          )}
          {!!message.inputTokenCount && (
            <div className="message_metadata_item">
              <ArrowUpIcon color="rgb(109, 123, 147)" size="16" />{" "}
              {message.inputTokenCount} <Message id="message_tokens" />
            </div>
          )}
          {!!message.outputTokenCount && (
            <div className="message_metadata_item">
              <ArrowDownIcon color="rgb(109, 123, 147)" size="16" />{" "}
              {message.outputTokenCount} <Message id="message_tokens" />
            </div>
          )}
        </div>
        {!editing && hovering && (
          <div className="message_cover">
            <div className="message_actions">
              <button
                className="btn btn-sm no-btn tile-action"
                onClick={onCopy}
              >
                {messageCopied && (
                  <CheckIcon size="16" color="#344767" strokeWidth="2" />
                )}
                {!messageCopied && <ClipboardIcon size="16" color="#344767" />}
                <Message id="copy" />
              </button>
              <button
                className="btn btn-sm no-btn tile-action"
                onClick={startEditing}
              >
                <Edit2Icon size="16" color="#344767" />
              </button>
              {!deleting && (
                <button
                  className="btn btn-sm no-btn tile-action"
                  onClick={startDeleting}
                >
                  <TrashIcon size="16" color="#344767" />
                </button>
              )}
              {deleting && (
                <button
                  className="btn bg-gradient-primary btn-sm"
                  onClick={onDeleteConfirmed}
                >
                  <Message id="confirm_delete" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ChatMessage;
